/**
 * Environment validation utilities
 */

import { DEFAULT_API_BASE_URL, ENV_VALIDATION_HELP_TEXT } from './constants';

export interface EnvConfig {
  googleRecaptchaSiteKey: string;
  apiBaseUrl: string;
  environment: string;
  enableAnalytics: boolean;
  enableDebug: boolean;
  enableI18n: boolean;
  // Mixpanel
  mixpanelToken?: string;
  mixpanelApiHost?: string; // hostname only, e.g. telemetry.ag1.evidence.com
  mixpanelApiEndpoint?: string; // path only, default '/api/telemetry/mixpanel'
  appName?: string;
  region?: string;
  // Chatbot-specific configuration
  chatbotApiKey?: string;
  chatbotTimeoutMs?: number;
  chatbotRetryAttempts?: number;
}

/**
 * Custom error class for environment validation errors
 */
export class EnvironmentValidationError extends Error {
  constructor(
    message: string,
    public missingVariables: string[] = [],
    public invalidVariables: string[] = []
  ) {
    super(message);
    this.name = 'EnvironmentValidationError';
  }
}

/**
 * Validates a required environment variable
 * @param name - The environment variable name
 * @param value - The environment variable value
 * @param validator - Optional validator function
 * @returns The validated value
 */
function validateRequiredEnvVar(
  name: string,
  value: string | undefined,
  validator?: (value: string) => boolean
): string {
  if (!value || value.trim() === '') {
    throw new EnvironmentValidationError(
      `Missing required environment variable: ${name}`,
      [name]
    );
  }

  const trimmedValue = value.trim();

  if (validator && !validator(trimmedValue)) {
    throw new EnvironmentValidationError(
      `Invalid value for environment variable: ${name}`,
      [],
      [name]
    );
  }

  return trimmedValue;
}

/**
 * Validates an optional environment variable with a default value
 * @param name - The environment variable name
 * @param value - The environment variable value
 * @param defaultValue - The default value to use if not provided
 * @param validator - Optional validator function
 * @returns The validated value or default
 */
function validateOptionalEnvVar<T>(
  name: string,
  value: string | undefined,
  defaultValue: T,
  validator?: (value: string) => boolean
): T | string {
  if (!value || value.trim() === '') {
    return defaultValue;
  }

  const trimmedValue = value.trim();

  if (validator && !validator(trimmedValue)) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid value for optional environment variable: ${name}, using default: ${defaultValue}`);
    return defaultValue;
  }

  return trimmedValue;
}

/**
 * URL validator function
 * @param value - The URL to validate
 * @returns true if valid URL, false otherwise
 */
function isValidUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Boolean string validator function
 * @param value - The boolean string to validate
 * @returns true if valid boolean string, false otherwise
 */
function isValidBoolean(value: string): boolean {
  return ['true', 'false', '1', '0'].includes(value.toLowerCase());
}

/**
 * Environment validator function
 * @param value - The environment to validate
 * @returns true if valid environment, false otherwise
 */
function isValidEnvironment(value: string): boolean {
  return ['development', 'production', 'test', 'staging'].includes(value.toLowerCase());
}

/**
 * Converts string boolean to actual boolean
 * @param value - The string to convert
 * @returns boolean value
 */
function stringToBoolean(value: string): boolean {
  return ['true', '1'].includes(value.toLowerCase());
}

/**
 * Validates all environment variables and returns a typed configuration object
 * @returns Validated environment configuration
 * @throws EnvironmentValidationError if validation fails
 */
export function validateEnvironmentVariables(): EnvConfig {
  const errors: string[] = [];
  const missingVars: string[] = [];
  const invalidVars: string[] = [];

  try {
    // Required: Google reCAPTCHA Site Key
    const googleRecaptchaSiteKey = validateRequiredEnvVar(
      'REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY',
      process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY,
      (value) => value.length > 20 // Basic length check for reCAPTCHA keys
    );

    // API Base URL (use default if not provided)
    const apiBaseUrl = validateOptionalEnvVar(
      'REACT_APP_FUSUS_CORE_SETUP_API_BASE_URL',
      process.env.REACT_APP_FUSUS_CORE_SETUP_API_BASE_URL,
      DEFAULT_API_BASE_URL,
      isValidUrl
    ) as string;

    // Optional: Environment (defaults to development)
    const environment = validateOptionalEnvVar(
      'REACT_APP_ENV',
      process.env.REACT_APP_ENV || process.env.NODE_ENV,
      'development',
      isValidEnvironment
    ) as string;

    // Optional: Enable Analytics (defaults to false)
    const enableAnalyticsStr = validateOptionalEnvVar(
      'REACT_APP_ENABLE_ANALYTICS',
      process.env.REACT_APP_ENABLE_ANALYTICS,
      'false',
      isValidBoolean
    ) as string;
    const enableAnalytics = stringToBoolean(enableAnalyticsStr);

    // Optional: Enable Debug (defaults to true in development)
    const enableDebugStr = validateOptionalEnvVar(
      'REACT_APP_ENABLE_DEBUG',
      process.env.REACT_APP_ENABLE_DEBUG,
      environment === 'development' ? 'true' : 'false',
      isValidBoolean
    ) as string;
    const enableDebug = stringToBoolean(enableDebugStr);

    // Optional: Enable I18n (defaults to false)
    const enableI18nStr = validateOptionalEnvVar(
      'REACT_APP_ENABLE_I18N',
      process.env.REACT_APP_ENABLE_I18N,
      'false',
      isValidBoolean
    ) as string;
    const enableI18n = stringToBoolean(enableI18nStr);

    // Optional: Mixpanel Token
    const mixpanelToken = validateOptionalEnvVar(
      'REACT_APP_MIXPANEL_TOKEN',
      process.env.REACT_APP_MIXPANEL_TOKEN,
      ''
    ) as string;

    // Optional: Mixpanel API Host (hostname only)
    const mixpanelApiHost = validateOptionalEnvVar(
      'REACT_APP_MIXPANEL_API_HOST',
      process.env.REACT_APP_MIXPANEL_API_HOST,
      ''
    ) as string;

    // Optional: Mixpanel API Endpoint (path only)
    const mixpanelApiEndpoint = validateOptionalEnvVar(
      'REACT_APP_MIXPANEL_API_ENDPOINT',
      process.env.REACT_APP_MIXPANEL_API_ENDPOINT,
      '/api/telemetry/mixpanel'
    ) as string;

    // Optional: App name and region for analytics context
    const appName = validateOptionalEnvVar(
      'REACT_APP_APP_NAME',
      process.env.REACT_APP_APP_NAME,
      'Fusus Core Setup'
    ) as string;

    const region = validateOptionalEnvVar(
      'REACT_APP_REGION',
      process.env.REACT_APP_REGION,
      'unknown'
    ) as string;

    // Optional: Chatbot API Key
    const chatbotApiKey = validateOptionalEnvVar(
      'REACT_APP_CHATBOT_API_KEY',
      process.env.REACT_APP_CHATBOT_API_KEY,
      'dev-chatbot-key'
    ) as string;

    // Optional: Chatbot Timeout (defaults to 60 seconds)
    const chatbotTimeoutMsStr = validateOptionalEnvVar(
      'REACT_APP_CHATBOT_TIMEOUT_MS',
      process.env.REACT_APP_CHATBOT_TIMEOUT_MS,
      '60000',
      (value) => !isNaN(Number(value)) && Number(value) > 0
    ) as string;
    const chatbotTimeoutMs = Number(chatbotTimeoutMsStr);

    // Optional: Chatbot Retry Attempts (defaults to 3)
    const chatbotRetryAttemptsStr = validateOptionalEnvVar(
      'REACT_APP_CHATBOT_RETRY_ATTEMPTS',
      process.env.REACT_APP_CHATBOT_RETRY_ATTEMPTS,
      '3',
      (value) => !isNaN(Number(value)) && Number(value) >= 0
    ) as string;
    const chatbotRetryAttempts = Number(chatbotRetryAttemptsStr);

    return {
      googleRecaptchaSiteKey,
      apiBaseUrl,
      environment,
      enableAnalytics,
      enableDebug,
      enableI18n,
      mixpanelToken,
      mixpanelApiHost,
      mixpanelApiEndpoint,
      appName,
      region,
      chatbotApiKey,
      chatbotTimeoutMs,
      chatbotRetryAttempts,
    };

  } catch (error) {
    if (error instanceof EnvironmentValidationError) {
      missingVars.push(...error.missingVariables);
      invalidVars.push(...error.invalidVariables);
      errors.push(error.message);
    } else {
      errors.push(`Unexpected error during environment validation: ${error}`);
    }
  }

  // If there are any validation errors, throw a comprehensive error
  if (errors.length > 0) {
    const errorMessage = [
      'Environment validation failed:',
      ...errors,
      '',
      ENV_VALIDATION_HELP_TEXT.CHECK_VARIABLES_MESSAGE,
      '',
      ENV_VALIDATION_HELP_TEXT.REQUIRED_VARIABLES,
      `- REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY: ${ENV_VALIDATION_HELP_TEXT.RECAPTCHA_DESCRIPTION}`,
      '',
      ENV_VALIDATION_HELP_TEXT.OPTIONAL_VARIABLES,
      `- REACT_APP_FUSUS_CORE_SETUP_API_BASE_URL: ${ENV_VALIDATION_HELP_TEXT.API_BASE_URL_DESCRIPTION}`,
      `- REACT_APP_ENV: ${ENV_VALIDATION_HELP_TEXT.ENV_DESCRIPTION}`,
      `- REACT_APP_ENABLE_ANALYTICS: ${ENV_VALIDATION_HELP_TEXT.ANALYTICS_DESCRIPTION}`,
      `- REACT_APP_ENABLE_DEBUG: ${ENV_VALIDATION_HELP_TEXT.DEBUG_DESCRIPTION}`,
    ].join('\n');

    throw new EnvironmentValidationError(errorMessage, missingVars, invalidVars);
  }

  // This should never be reached, but TypeScript requires it
  throw new Error('Unexpected error in environment validation');
}

/**
 * Cached environment configuration
 */
let cachedEnvConfig: EnvConfig | null = null;

/**
 * Gets the validated environment configuration
 * Validates only once and caches the result
 * @returns Validated environment configuration
 */
export function getEnvConfig(): EnvConfig {
  if (!cachedEnvConfig) {
    cachedEnvConfig = validateEnvironmentVariables();
  }
  return cachedEnvConfig;
}

/**
 * Resets the cached environment configuration
 * Useful for testing or when environment variables change
 */
export function resetEnvConfig(): void {
  cachedEnvConfig = null;
} 